// Card-back skin renderer. Returns inline-style + content for the
// hidden card-back (opponent's drawn / face-down deck) based on the
// player's active_skin selection.
//
// Layout: all skins share the same structure —
//   ┌─────────────┐
//   │ Q       ←brand in corner (always 1 letter, always fits)
//   │             │
//   │      ♣      ←suit at center
//   │             │
//   │       TAG ← ←skin-specific short tagline (bottom-right)
//   └─────────────┘
//
// The Q brand + short tagline eliminates the "QUATTRO" text-clipping problem
// we had before at 72px-wide preview tiles.

import React from "react";

export const SKIN_LABELS = {
  default: "Classic",
  gold_card_back: "Gold Royal",
  neon_holo: "Neon Holo",
  royal_crimson: "Royal Crimson",
  galaxy: "Galaxy",
  cherry_blossom: "Cherry Blossom",
};

export const SKIN_DESCRIPTIONS = {
  default: "The original Quattro card back.",
  gold_card_back: "Brushed-gold guilloché with a regal monogram.",
  neon_holo: "Holographic prism — refracts the player's color into a rainbow shimmer.",
  royal_crimson: "Burgundy velvet with embossed crest.",
  galaxy: "Deep space — nebula swirls and starlight.",
  cherry_blossom: "Pink sakura petals on parchment.",
};

// Shared face shell — handles size variants.
//   sm = PlayerHUD thumbnail (~28×40). No brand/tag, just the suit.
//   md = cosmetics modal preview (~72×96). Brand in corner + tag in bottom.
//   lg = confirm-buy / future hero view. Same as md but bigger suit.
function Face({ size, style, brandColor, tag, tagColor, children }) {
  const isSm = size === "sm";
  const padding = isSm ? "1px 2px" : "4px 5px";
  const borderRadius = isSm ? 4 : 10;
  return (
    <div
      className={isSm ? "" : "card-face"}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding,
        fontFamily: "'Unbounded', sans-serif",
        fontWeight: 800,
        overflow: "hidden",
        boxSizing: "border-box",
        ...style,
      }}
    >
      {!isSm && (
        <span style={{
          position: "absolute",
          top: 4, left: 5,
          fontSize: 8, fontWeight: 900,
          letterSpacing: "0.02em",
          opacity: 0.9,
          color: brandColor,
          lineHeight: 1,
        }}>Q</span>
      )}
      {children}
      {!isSm && tag && (
        <span style={{
          position: "absolute",
          bottom: 3, right: 5,
          fontSize: 6,
          letterSpacing: "0.08em",
          textAlign: "right",
          whiteSpace: "nowrap",
          maxWidth: "calc(100% - 12px)",
          overflow: "hidden",
          opacity: 0.8,
          color: tagColor,
          lineHeight: 1,
        }}>{tag}</span>
      )}
    </div>
  );
}

export function CardBack({ skin = "default", color = "#FFD700", suit = "♣", size = "md" }) {
  const isLg = size === "lg";
  const isSm = size === "sm";
  const fontLg = isLg ? 32 : isSm ? 16 : 26;

  const suitNode = (extra = {}) => (
    <div style={{ fontSize: fontLg, textAlign: "center", fontWeight: 900, lineHeight: 1, ...extra }}>
      {suit}
    </div>
  );

  if (skin === "gold_card_back") {
    return (
      <Face
        size={size}
        brandColor="#FFD700"
        tag="ROYAL"
        tagColor="#FFD700"
        style={{
          background:
            "repeating-linear-gradient(45deg, rgba(255,215,0,0.18) 0 4px, rgba(120,80,0,0.12) 4px 8px)," +
            "radial-gradient(circle at 50% 50%, rgba(255,215,0,0.35), transparent 60%)," +
            "linear-gradient(155deg, #2a1d00, #0B0C10)",
          border: "1.5px solid rgba(255,215,0,0.7)",
          boxShadow: "inset 0 0 12px rgba(255,215,0,0.35)",
          color: "#FFD700",
        }}
      >
        {suitNode({ textShadow: "0 0 12px rgba(255,215,0,0.7)" })}
      </Face>
    );
  }

  if (skin === "neon_holo") {
    return (
      <Face
        size={size}
        brandColor="#fff"
        tag="HOLO"
        tagColor="#00E5FF"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,0,170,0.22) 0%, rgba(0,229,255,0.22) 50%, rgba(181,55,242,0.22) 100%)," +
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 4px)," +
            "#0B0C10",
          border: `1.5px solid ${color}`,
          boxShadow: `inset 0 0 18px ${color}55`,
          color: "#fff",
        }}
      >
        {suitNode({ color })}
      </Face>
    );
  }

  if (skin === "royal_crimson") {
    return (
      <Face
        size={size}
        brandColor="#FFD0D0"
        tag="CRIMSON"
        tagColor="#FFD0D0"
        style={{
          background:
            "radial-gradient(circle at 30% 25%, rgba(255,80,80,0.35), transparent 55%)," +
            "repeating-linear-gradient(135deg, rgba(120,10,30,0.4) 0 5px, rgba(60,5,15,0.4) 5px 10px)," +
            "linear-gradient(155deg, #4a0a18, #1a0306)",
          border: "1.5px solid rgba(220,60,80,0.7)",
          boxShadow: "inset 0 0 14px rgba(255,80,80,0.4)",
          color: "#FFD0D0",
        }}
      >
        {suitNode({ textShadow: "0 0 10px rgba(255,100,120,0.7)" })}
      </Face>
    );
  }

  if (skin === "galaxy") {
    return (
      <Face
        size={size}
        brandColor="#E5D4FF"
        tag="COSMOS"
        tagColor="#B5E0FF"
        style={{
          background:
            "radial-gradient(circle at 25% 25%, #fff 0 1px, transparent 1.5px)," +
            "radial-gradient(circle at 70% 65%, #fff 0 1px, transparent 1.5px)," +
            "radial-gradient(circle at 45% 80%, #fff 0 0.8px, transparent 1.2px)," +
            "radial-gradient(circle at 80% 20%, #fff 0 0.6px, transparent 1px)," +
            "radial-gradient(circle at 35% 50%, rgba(181,55,242,0.55), transparent 60%)," +
            "radial-gradient(circle at 75% 75%, rgba(0,229,255,0.4), transparent 50%)," +
            "linear-gradient(155deg, #0a0030, #000010)",
          border: "1.5px solid rgba(181,55,242,0.7)",
          boxShadow: "inset 0 0 18px rgba(181,55,242,0.35)",
          color: "#E5D4FF",
        }}
      >
        {suitNode({ textShadow: "0 0 12px rgba(181,55,242,0.8)" })}
      </Face>
    );
  }

  if (skin === "cherry_blossom") {
    return (
      <Face
        size={size}
        brandColor="#9c2c4a"
        tag="桜"
        tagColor="#9c2c4a"
        style={{
          background:
            "radial-gradient(circle at 25% 30%, rgba(255,182,193,0.6) 0 4px, transparent 5px)," +
            "radial-gradient(circle at 75% 65%, rgba(255,200,210,0.5) 0 3px, transparent 4px)," +
            "radial-gradient(circle at 45% 80%, rgba(255,182,193,0.55) 0 3px, transparent 4px)," +
            "radial-gradient(circle at 60% 25%, rgba(255,200,210,0.45) 0 2px, transparent 3px)," +
            "linear-gradient(155deg, #fff5f7, #ffd9e0)",
          border: "1.5px solid rgba(255,150,170,0.8)",
          boxShadow: "inset 0 0 14px rgba(255,182,193,0.55)",
          color: "#9c2c4a",
        }}
      >
        {suitNode()}
      </Face>
    );
  }

  // Default "Classic"
  return (
    <Face
      size={size}
      brandColor={color}
      tag="CLASSIC"
      tagColor={color}
      style={{
        background: `repeating-linear-gradient(45deg, ${color}22 0 6px, transparent 6px 12px), #0B0C10`,
        border: `1px solid ${color}55`,
        color,
      }}
    >
      {suitNode()}
    </Face>
  );
}
